import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, Tooltip, IconButton,
  LinearProgress,
} from '@mui/material';
import {
  Shield, Add, Edit, Delete, Warning, CheckCircle,
  ErrorOutline, AccessTime, InfoOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const DOC_TYPES: Record<string, { label: string; emoji: string }> = {
  passport:          { label: 'Passport',           emoji: '🛂' },
  national_id:       { label: 'National ID',         emoji: '🪪' },
  work_permit:       { label: 'Work Permit',          emoji: '📋' },
  visa:              { label: 'Visa',                 emoji: '✈️' },
  dependent_visa:    { label: 'Dependent Visa',       emoji: '👨‍👩‍👧' },
  professional_cert: { label: 'Professional Cert',   emoji: '🎓' },
  medical_cert:      { label: 'Medical Certificate', emoji: '🏥' },
  driving_license:   { label: 'Driving License',     emoji: '🚗' },
  right_to_work:     { label: 'Right to Work',        emoji: '✅' },
  first_aid_cert:    { label: 'First Aid Cert',       emoji: '🩺' },
  other:             { label: 'Other',                emoji: '📄' },
};

const BAND_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  expired:  { label: 'Expired',          color: '#c62828', bg: '#ffebee', icon: <ErrorOutline /> },
  critical: { label: 'Expires ≤30d',     color: '#e65100', bg: '#fff3e0', icon: <Warning /> },
  warning:  { label: 'Expires ≤90d',     color: '#f57f17', bg: '#fffde7', icon: <AccessTime /> },
  valid:    { label: 'Valid',            color: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircle /> },
  no_expiry:{ label: 'No Expiry',        color: '#546e7a', bg: '#eceff1', icon: <InfoOutlined /> },
};

interface CompDoc {
  id: string; employeeId: string; docType: string;
  docNumber?: string; issuingAuthority?: string; countryOfIssue?: string;
  issueDate?: string; expiryDate?: string; notes?: string;
  band: string; daysUntilExpiry: number | null;
  employee?: { firstName: string; lastName: string; department?: { name: string }; position?: { title: string } };
}

interface Dashboard {
  total: number; expired: number; critical: number; warning: number;
  valid: number; noExpiry: number;
  byType: { docType: string; count: string }[];
  urgentItems: CompDoc[];
}

const emptyForm = {
  employeeId: '', docType: 'passport', docNumber: '',
  issuingAuthority: '', countryOfIssue: '',
  issueDate: '', expiryDate: '', notes: '', fileUrl: '',
};

export default function ComplianceDocs() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');
  const myEmpId = (user as any)?.employee?.id;

  const [tab, setTab] = useState(0);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [docs, setDocs] = useState<CompDoc[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [empFilter, setEmpFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [bandFilter, setBandFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (!isHR) params.set('employeeId', myEmpId);
      else if (empFilter) params.set('employeeId', empFilter);
      if (typeFilter) params.set('docType', typeFilter);

      const calls: Promise<any>[] = [apiFetch(`/api/compliance-docs?${params}`)];
      if (isHR) {
        calls.push(apiFetch('/api/compliance-docs/dashboard'));
        calls.push(apiFetch('/api/employees?limit=300'));
      }
      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setDocs(results[0].value ?? []);
      if (results[1] && results[1].status === 'fulfilled') setDashboard((results[1] as any).value);
      if (results[2] && results[2].status === 'fulfilled') {
        const d = (results[2] as any).value;
        setEmployees(Array.isArray(d) ? d : (d.data ?? []));
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [isHR, myEmpId, empFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = (preEmpId = '') => {
    setEditId(null);
    setFormData({ ...emptyForm, employeeId: preEmpId });
    setFormOpen(true);
  };

  const openEdit = (doc: CompDoc) => {
    setEditId(doc.id);
    setFormData({
      employeeId: doc.employeeId, docType: doc.docType,
      docNumber: doc.docNumber || '', issuingAuthority: doc.issuingAuthority || '',
      countryOfIssue: doc.countryOfIssue || '', issueDate: doc.issueDate || '',
      expiryDate: doc.expiryDate || '', notes: doc.notes || '', fileUrl: (doc as any).fileUrl || '',
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(formData).filter(([, v]) => v !== ''));
      if (editId) {
        await apiFetch(`/api/compliance-docs/${editId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await apiFetch('/api/compliance-docs', { method: 'POST', body: JSON.stringify(payload) });
      }
      setFormOpen(false);
      load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    await apiFetch(`/api/compliance-docs/${deleteId}`, { method: 'DELETE' });
    setDeleteId(null);
    load();
  };

  const filtered = docs.filter(d => {
    if (bandFilter && d.band !== bandFilter) return false;
    return true;
  });

  function BandChip({ band }: { band: string }) {
    const cfg = BAND_CONFIG[band] ?? BAND_CONFIG.no_expiry;
    return (
      <Chip size="small" icon={cfg.icon as any}
        label={cfg.label}
        sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.7rem',
          '& .MuiChip-icon': { color: `${cfg.color} !important`, fontSize: '0.9rem' } }} />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#1b5e20', width: 48, height: 48 }}><Shield /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Compliance Documents</Typography>
            <Typography variant="body2" color="text.secondary">
              Track passports, visas, work permits & certifications
            </Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />} onClick={() => openCreate()}
            sx={{ bgcolor: '#1b5e20', '&:hover': { bgcolor: '#145a14' } }}>
            Add Document
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Dashboard stats (HR) */}
      {isHR && dashboard && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { key: 'expired',  title: 'Expired',       value: dashboard.expired,  color: BAND_CONFIG.expired.color,   bg: BAND_CONFIG.expired.bg },
              { key: 'critical', title: '≤30 Days',      value: dashboard.critical, color: BAND_CONFIG.critical.color,  bg: BAND_CONFIG.critical.bg },
              { key: 'warning',  title: '≤90 Days',      value: dashboard.warning,  color: BAND_CONFIG.warning.color,   bg: BAND_CONFIG.warning.bg },
              { key: 'valid',    title: 'Valid',          value: dashboard.valid,    color: BAND_CONFIG.valid.color,     bg: BAND_CONFIG.valid.bg },
              { key: 'no_expiry',title: 'No Expiry',     value: dashboard.noExpiry, color: BAND_CONFIG.no_expiry.color, bg: BAND_CONFIG.no_expiry.bg },
              { key: '',         title: 'Total Tracked', value: dashboard.total,    color: '#1565c0', bg: '#e3f2fd' },
            ].map(s => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={s.title}>
                <Paper
                  onClick={() => s.key && setBandFilter(bandFilter === s.key ? '' : s.key)}
                  sx={{
                    p: 1.5, textAlign: 'center', cursor: s.key ? 'pointer' : 'default',
                    borderTop: `3px solid ${s.color}`,
                    outline: bandFilter === s.key ? `2px solid ${s.color}` : 'none',
                    '&:hover': s.key ? { boxShadow: 3 } : {},
                  }}
                >
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.title}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Urgent alert bar */}
          {(dashboard.expired > 0 || dashboard.critical > 0) && (
            <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
              <strong>{dashboard.expired + dashboard.critical} document{dashboard.expired + dashboard.critical !== 1 ? 's' : ''}</strong> require immediate attention —
              {dashboard.expired > 0 && ` ${dashboard.expired} expired`}
              {dashboard.expired > 0 && dashboard.critical > 0 && ','}
              {dashboard.critical > 0 && ` ${dashboard.critical} expiring within 30 days`}.
            </Alert>
          )}
        </>
      )}

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {isHR && (
          <TextField select size="small" label="Employee" value={empFilter}
            onChange={e => setEmpFilter(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">All Employees</MenuItem>
            {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</MenuItem>)}
          </TextField>
        )}
        <TextField select size="small" label="Document Type" value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All Types</MenuItem>
          {Object.entries(DOC_TYPES).map(([k, v]) => (
            <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
          ))}
        </TextField>
        {isHR && bandFilter && (
          <Chip label={`Showing: ${BAND_CONFIG[bandFilter]?.label}`} onDelete={() => setBandFilter('')}
            sx={{ bgcolor: BAND_CONFIG[bandFilter]?.bg, color: BAND_CONFIG[bandFilter]?.color }} />
        )}
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {isHR && <TableCell>Employee</TableCell>}
                <TableCell>Type</TableCell>
                <TableCell>Number</TableCell>
                <TableCell>Issuing Authority</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Issue Date</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Days Left</TableCell>
                {isHR && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={isHR ? 10 : 8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No documents found
                </TableCell></TableRow>
              )}
              {filtered.map(doc => (
                <TableRow key={doc.id} hover
                  sx={{ bgcolor: doc.band === 'expired' ? '#ffebee40' : doc.band === 'critical' ? '#fff3e040' : undefined }}>
                  {isHR && (
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {doc.employee?.firstName} {doc.employee?.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {doc.employee?.department?.name}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2">
                      {DOC_TYPES[doc.docType]?.emoji} {DOC_TYPES[doc.docType]?.label ?? doc.docType}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2">{doc.docNumber || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{doc.issuingAuthority || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{doc.countryOfIssue || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{doc.issueDate || '—'}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={doc.band !== 'valid' && doc.band !== 'no_expiry' ? 700 : 400}
                      color={BAND_CONFIG[doc.band]?.color}>
                      {doc.expiryDate || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell><BandChip band={doc.band} /></TableCell>
                  <TableCell>
                    {doc.daysUntilExpiry !== null ? (
                      <Typography variant="body2" fontWeight={700}
                        color={BAND_CONFIG[doc.band]?.color}>
                        {doc.daysUntilExpiry < 0 ? `${Math.abs(doc.daysUntilExpiry)}d ago` : `${doc.daysUntilExpiry}d`}
                      </Typography>
                    ) : '—'}
                  </TableCell>
                  {isHR && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(doc)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteId(doc.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Document' : 'Add Compliance Document'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {isHR && !editId && (
            <TextField select fullWidth label="Employee" value={formData.employeeId}
              onChange={e => setFormData(p => ({ ...p, employeeId: e.target.value }))} required>
              {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</MenuItem>)}
            </TextField>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth label="Document Type" value={formData.docType}
              onChange={e => setFormData(p => ({ ...p, docType: e.target.value }))}>
              {Object.entries(DOC_TYPES).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth label="Document Number" value={formData.docNumber}
              onChange={e => setFormData(p => ({ ...p, docNumber: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Issuing Authority" value={formData.issuingAuthority}
              onChange={e => setFormData(p => ({ ...p, issuingAuthority: e.target.value }))} />
            <TextField fullWidth label="Country of Issue" value={formData.countryOfIssue}
              onChange={e => setFormData(p => ({ ...p, countryOfIssue: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Issue Date" type="date" InputLabelProps={{ shrink: true }}
              value={formData.issueDate} onChange={e => setFormData(p => ({ ...p, issueDate: e.target.value }))} />
            <TextField fullWidth label="Expiry Date" type="date" InputLabelProps={{ shrink: true }}
              value={formData.expiryDate} onChange={e => setFormData(p => ({ ...p, expiryDate: e.target.value }))} />
          </Box>
          <TextField fullWidth label="Notes" multiline rows={2} value={formData.notes}
            onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} />
          <TextField fullWidth label="File / Scan URL (optional)" value={formData.fileUrl}
            onChange={e => setFormData(p => ({ ...p, fileUrl: e.target.value }))}
            placeholder="https://..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}
            disabled={saving || !formData.docType || (!isHR ? false : !formData.employeeId)}
            sx={{ bgcolor: '#1b5e20' }}>
            {saving ? 'Saving…' : editId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Document?</DialogTitle>
        <DialogContent><Typography>This will remove the document record.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
